import { cineamoApiClient as apiClient } from '../../apiClient';
import { CartDto } from '../../models/cart/CartDto.types';
import { CartGiftCardDto } from '../../models/cart-gift-card/CartGiftCardDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { CartGiftCardListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

const cartSession = (cartUuid: CartDto['uuid']) => ({ scheme: 'CineamoCart', accessToken: cartUuid }) as const;

/**
 * Attaches a gift card to the cart. A cart carries one card at a time, so a new code replaces the old one.
 *
 * Resolves with the error response on failure instead of null, so the caller can tell the cases apart by
 * status: 404 unknown code, 410 no longer valid, 403 redemption not enabled for the cinema, 400 the
 * ticket system could not validate it.
 */
export async function setCartGiftCardByCode(
    cartUuid: CartDto['uuid'],
    code: string
): Promise<ApiResponse<CartGiftCardDto> | ApiErrorResponse> {
    return apiClient
        .put<ApiResponse<CartGiftCardDto>>(
            `/carts/${cartUuid}/gift-cards/by-code/${encodeURIComponent(code)}`,
            {},
            { session: cartSession(cartUuid) }
        )
        .then((response) => response.data)
        .catch((error) => error);
}

export async function deleteCartGiftCardByCode(
    cartUuid: CartDto['uuid'],
    code: string
): Promise<boolean | ApiErrorResponse> {
    return apiClient
        .delete(`/carts/${cartUuid}/gift-cards/by-code/${encodeURIComponent(code)}`, undefined, {
            session: cartSession(cartUuid)
        })
        .then((response) => response.status === 204)
        .catch((error) => error);
}

export async function getCartGiftCards(
    cartUuid: CartDto['uuid']
): Promise<ApiResponse<CartGiftCardListResponse> | null> {
    return apiClient
        .get(`/carts/${cartUuid}/gift-cards`, { session: cartSession(cartUuid) })
        .then((response) => response.data as ApiResponse<CartGiftCardListResponse>)
        .catch(() => null);
}
